// src/pages/events/components/rsvp-form.tsx
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { RSVPEntry } from "@/lib/api/types";
import { Loader2 } from "lucide-react";
import { KENYAN_PHONE_PATTERN, normalizeKenyanPhone } from "@/lib/utils/events";

const phoneField = z
  .string()
  .min(1, "Phone number is required")
  .transform(normalizeKenyanPhone)
  .refine((value) => KENYAN_PHONE_PATTERN.test(value), "Enter a valid Kenyan number, e.g. 0712 345 678");

const formSchema = z.object({
  fullName: z.string().trim().min(2, "Full name is required"),
  email: z.string().trim().toLowerCase().email("Invalid email address"),
  mpesaPhone: phoneField,
  whatsappPhone: phoneField,
});

type FormInput = z.input<typeof formSchema>;
export type RSVPFormValues = z.output<typeof formSchema>;

interface RSVPFormProps {
  initialData?: RSVPEntry | null;
  onSubmit: (data: RSVPFormValues) => Promise<void>;
  onCancel: () => void;
}

const fields: { name: keyof FormInput; label: string; type?: string; placeholder: string }[] = [
  { name: "fullName", label: "Full Name", placeholder: "Attendee's full name" },
  { name: "email", label: "Email Address", type: "email", placeholder: "name@example.com" },
  { name: "mpesaPhone", label: "M-Pesa Phone Number", type: "tel", placeholder: "0712 345 678" },
  { name: "whatsappPhone", label: "WhatsApp Number", type: "tel", placeholder: "0712 345 678" },
];

export function RSVPForm({ initialData, onSubmit, onCancel }: RSVPFormProps) {
  const form = useForm<FormInput, unknown, RSVPFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: initialData?.fullName || "",
      email: initialData?.email || "",
      mpesaPhone: initialData?.mpesaPhone || "",
      whatsappPhone: initialData?.whatsappPhone || "",
    },
  });

  const handleSubmit = async (values: RSVPFormValues) => {
    try {
      await onSubmit(values);
    } catch {
      // Errors are surfaced by the API client's toast
    }
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5 text-foreground">
      {fields.map((field) => {
        const error = form.formState.errors[field.name]?.message;
        return (
          <div key={field.name} className="space-y-1.5">
            <Label htmlFor={field.name}>
              {field.label} <span className="text-destructive">*</span>
            </Label>
            <Input
              id={field.name}
              type={field.type ?? "text"}
              placeholder={field.placeholder}
              aria-invalid={!!error}
              className={error ? "border-destructive" : undefined}
              {...form.register(field.name)}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        );
      })}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={form.formState.isSubmitting} className="min-w-[130px]">
          {form.formState.isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : initialData ? (
            "Save Changes"
          ) : (
            "Add Attendee"
          )}
        </Button>
      </div>
    </form>
  );
}
